import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Loader2, Dog, Cat, ExternalLink, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import { BreadcrumbSchema } from '@/components/BreadcrumbSchema';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';
const BASE_URL = window.location.origin;

export default function KennelsByLocation() {
  const { t } = useTranslation();
  const { breed, voivodeship } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [breed, voivodeship]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/public/hodowle/${breed}/${voivodeship}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const { breed: breedName, voivodeship: voivName, kennels, count, seo } = data || {};

  return (
    <>
      <Helmet>
        <title>{seo?.title || `Hodowle ${breedName} ${voivName}`}</title>
        <meta name="description" content={seo?.description} />
        <link rel="canonical" href={`${BASE_URL}${seo?.canonical}`} />
        {seo?.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
        {seo?.og && (
          <>
            <meta property="og:title" content={seo.og.title} />
            <meta property="og:description" content={seo.og.description} />
            <meta property="og:type" content={seo.og.type} />
            <meta property="og:url" content={seo.og.url} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.og.title} />
            <meta name="twitter:description" content={seo.og.description} />
          </>
        )}
      </Helmet>
      <BreadcrumbSchema breadcrumbs={seo?.breadcrumbs} />

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-10 h-10" />
              <span className="font-semibold text-xl text-white">ApisAI</span>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-500 hover:bg-blue-600 rounded-full px-6">Dodaj swoją hodowlę</Button>
            </Link>
          </div>
        </nav>

        {/* Breadcrumbs */}
        <div className="pt-24 pb-4">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              {seo?.breadcrumbs?.map((b, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="w-4 h-4" />}
                  {i === seo.breadcrumbs.length - 1 ? (
                    <span className="text-white">{b.name}</span>
                  ) : (
                    <Link to={b.url} className="hover:text-white">{b.name}</Link>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="page-title">
                {seo?.h1 || `Hodowle ${breedName}`}
              </h1>
              <p className="text-xl text-gray-400">{count} zarejestrowanych hodowców</p>
            </motion.div>

            {kennels?.length > 0 ? (
              <div className="grid gap-4">
                {kennels.map((kennel, i) => (
                  <motion.div key={kennel.id || i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Link to={`/hodowla/${kennel.slug}`} className="block glass-card rounded-2xl p-6 hover:border-blue-500/30 transition-colors group">
                      <div className="flex items-center gap-6">
                        <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          {kennel.species?.includes('cat') ? (
                            <Cat className="w-8 h-8 text-amber-400" />
                          ) : (
                            <Dog className="w-8 h-8 text-blue-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">{kennel.name}</h3>
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <MapPin className="w-4 h-4" />
                            {kennel.city}
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-400" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-2xl p-12 text-center">
                <Dog className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Brak hodowli w tym regionie</h3>
                <p className="text-gray-400 mb-6">Bądź pierwszy! Dodaj swoją hodowlę.</p>
                <Link to={`/rasy/${breed}`}>
                  <Button variant="outline" className="rounded-full mr-4">Zobacz inne regiony</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-blue-500 hover:bg-blue-600 rounded-full">Dodaj hodowlę</Button>
                </Link>
              </div>
            )}

            {/* Internal Links */}
            <div className="mt-12">
              <h2 className="text-xl font-bold text-white mb-6">Powiązane</h2>
              <div className="flex flex-wrap gap-3">
                <Link to={`/rasy/${breed}`} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  Wszystkie {breedName}
                </Link>
                <Link to={`/mioty/${breed}/${voivodeship}`} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  Mioty {breedName} — {voivName}
                </Link>
                <Link to="/katalog/uslugi" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
                  Katalog Firm i Usług
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-4">
              <img src="/logo.svg" alt="ApisAI" className="w-8 h-8" />
              <span className="font-semibold text-white">ApisAI</span>
            </Link>
            <p className="text-gray-500 text-sm">Profesjonalna promocja hodowli psów i kotów</p>
          </div>
        </footer>
        
        {/* ApisAI System Footer */}
        <ApisAIFooter variant="light" pageType="location" pageSlug={`${breed}-${voivodeship}`} />
      </div>
    </>
  );
}
